import { notification } from 'antd';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { selectUser } from '../../features/authentication/redux/selectors';
import { useVariantStep } from '../../features/variant_workflow/hooks/use_variant_step';
import { useVariantAccess } from '../../features/variant_workflow/hooks/useVariantAccess';
import {
  selectIsUserInstituteAllowed,
  selectIsVariantReadOnly,
} from '../../features/variant_workflow/redux';
import { displayUnAuthorizedNotification } from '../utils/notifications';

interface UseModalFormOptions {
  onClose: () => void;
  onSuccess?: () => void;
  successMessage?: string;
  errorMessage?: string;
  stageType?: string;
  /** When true (default), getIsSaveDisabled also checks canEditWorkflow (is_editor role).
   *  Set to false for modals that use a different permission (e.g. canEditMetadata). */
  requireWorkflowEdit?: boolean;
}

/**
 * useModalForm - Foundation Hook for Modal Form State Management
 *
 * PURPOSE:
 * This is a composition hook that encapsulates common modal form logic patterns
 * that were previously duplicated across 20+ modal components. It centralizes:
 * - Loading state management for async operations
 * - User authentication checks from Redux
 * - Stage information retrieval (via useVariantStep)
 * - Modal close prevention during loading
 * - Standardized save button disable logic
 * - Modal configuration factory
 *
 * RELATIONSHIP WITH OTHER HOOKS:
 * - Uses `useVariantStep` internally to get stage information
 * - Does NOT duplicate variant data fetching (handled by the variant route
 *   loader in Startup.tsx and cached by RTK Query)
 * - Acts as a "base" hook that modal-specific logic hooks build upon
 *
 * REFACTORING CONTEXT:
 * Created to apply React best practices by separating UI from logic.
 * Each modal component now has a corresponding `use*ModalLogic.ts` hook
 * that uses this foundation hook to eliminate redundant code.
 *
 *
 * USAGE EXAMPLE:
 * ```typescript
 * const {
 *   isLoading,           // Boolean - current loading state= is a save currently in progress?
 *   setIsLoading,        // Function - manually control loading state
 *   user,                // User object from Redux
 *   stageInfo,           // Stage information from useVariantStep
 *   handleModalClose,    // Function - closes modal (blocked during loading)
 *   handleSubmit,        // Function - wraps async submit with loading/notifications
 *   getIsSaveDisabled,   // Function - returns whether save button should be disabled
 *   createModalOpts,     // Function - creates standard modal configuration object
 * } = useModalForm({
 *   onClose: closeModal,
 *   successMessage: 'Operation completed successfully',
 *   errorMessage: 'Operation failed',
 *   stageType: 'clinical_observation',
 * });
 * ```
 *
 * @param options - Configuration options for the modal form
 * @param options.onClose - Required callback to close the modal
 * @param options.onSuccess - Optional callback after successful submit
 * @param options.successMessage - Message shown on success notification
 * @param options.errorMessage - Message shown on error notification (if provided)
 * @param options.stageType - Stage type for useVariantStep
 * @returns Object containing state and handlers for modal form management
 */
export const useModalForm = ({
  onClose,
  onSuccess,
  successMessage = 'Operation completed successfully',
  errorMessage,
  stageType: propStageType,
  requireWorkflowEdit = true,
}: UseModalFormOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector(selectUser);
  const isReadOnly = useSelector(selectIsVariantReadOnly);
  const isUserInstituteAllowed = useSelector(selectIsUserInstituteAllowed);
  const { canEditWorkflow } = useVariantAccess();
  const { stageType: urlStageType } = useParams();
  const stageInfo = useVariantStep(urlStageType || propStageType);

  /**
   * Handles modal close - prevents closing while loading
   */
  const handleModalClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    onClose();
  }, [isLoading, onClose]);

  /**
   * Wraps the submit function with loading state, error handling,
   * and success notification logic
   *
   * @param submitFn - The async function to execute on submit
   */
  const handleSubmit = useCallback(
    async (submitFn: () => Promise<void>) => {
      if (isLoading) {
        return;
      }

      if (!user) {
        displayUnAuthorizedNotification();
        return;
      }

      try {
        setIsLoading(true);
        await submitFn();
        notification.success({
          message: 'Success!',
          description: successMessage,
        });
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error(error);
        if (errorMessage) {
          notification.error({
            message: 'Error!',
            description: errorMessage,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, user, successMessage, errorMessage, onSuccess, onClose],
  );

  /**
   * Calculates whether the save button should be disabled
   * based on user authentication, stage status, form validity, loading state,
   * and variant lock status (read-only mode).
   *
   * @param isValid - Whether the form is valid
   * @param dirty - Whether the form has been modified
   * @returns Boolean indicating if save should be disabled
   */
  const getIsSaveDisabled = useCallback(
    (isValid: boolean, dirty: boolean) => {
      return (
        !user?.id ||
        !isUserInstituteAllowed ||
        isReadOnly ||
        (requireWorkflowEdit && !canEditWorkflow) ||
        (stageInfo?.status !== 'revised' &&
          stageInfo?.status !== 'not_started') ||
        !(isValid && dirty) ||
        isLoading
      );
    },
    [
      user,
      stageInfo,
      isLoading,
      isReadOnly,
      isUserInstituteAllowed,
      requireWorkflowEdit,
      canEditWorkflow,
    ],
  );

  /**
   * Creates the standard modal options object.
   * Width and height are intentionally left out so the CSS handles sizing
   * automatically: .modal__content { height: auto; width: min(90vw, 680px) }
   * Only pass height/width when you explicitly need to override (e.g. large tables).
   */
  const createModalOpts = useCallback(
    (title: React.ReactNode, height?: number, width?: number) => ({
      title,
      open: true,
      onCancel: () => {
        handleModalClose();
      },
      closable: true,
      footer: null,
      maskClosable: false,
      centered: true,
      draggable: true,
      ...(height !== undefined && { height }),
      ...(width !== undefined && { width }),
    }),
    [handleModalClose],
  );

  return {
    isLoading,
    setIsLoading,
    user,
    isReadOnly,
    stageInfo,
    handleModalClose,
    handleSubmit,
    getIsSaveDisabled,
    createModalOpts,
  };
};

export default useModalForm;
